import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import ListToggle from '@/features/sponsored-recommendations/components/ListToggle.vue';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { SraEventAction } from '@/@types/graphql-types';
import type {
	SRClickTitleEvent,
	ListSponsoredAd,
	SRClickEvent,
	ListTitleSR,
} from '@/features/sponsored-recommendations/types';
import SRTitleListGraphql from '@/components/SRTitleListGraphql.vue';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import type { Maybe } from '@/helpers/types';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';


export default /*#__PURE__*/_defineComponent({
  __name: 'SponsoredRecommendationTitleList',
  props: {
    sponsoredAd: null,
    lazy: { type: Boolean, default: true },
    fullWidth: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    placement: null
  },
  emits: ["onPosterClick", "onTrackerClickout", "imageError"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'onPosterClick', data: SRClickTitleEvent<ListTitleSR>): void;
	(e: 'onTrackerClickout', data?: SRClickEvent<ListTitleSR>): void;
	(e: 'imageError', src: Maybe<string>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { sponsoredAd: ListSponsoredAd, lazy: boolean, fullWidth: boolean, additionalContexts: SnowplowContext[], placement?: ClickoutPlacement };





const { trackSraEvent } = useBackendTracking();

const sponsoredCampaign = computed(() => props.sponsoredAd?.campaign);
const campaignNode = computed(() => sponsoredCampaign.value?.node);

// Template Variables
const promotionalTitle = computed(() => sponsoredCampaign.value?.promotionalTitle || campaignNode.value?.content.name);

const titles = computed(() => campaignNode.value?.titles.edges?.map(edge => edge.node) ?? []);

const landingPageURL = computed(() => sponsoredCampaign.value?.watchNowOffer.standardWebURL ?? '');

const isLandingPageEmpty = computed(() => !landingPageURL.value.trim().length);

function onSRListPosterClicked({ title, position }: SRClickEvent<ListTitleSR>) {
	// pass down click event after title got clicked
	emits('onPosterClick', { title, position });
}

function onSRListPosterClickedOut({ title, position }: SRClickEvent<ListTitleSR>) {
	trackSraEvent({
		action: SraEventAction.Clickout,
		sponsoredAd: () => props.sponsoredAd,
		jwEntityID: title.id,
		position,
	});
	emits('onTrackerClickout', { title, position });
}

function onSRListClick() {
	trackSraEvent({
		action: SraEventAction.Click,
		sponsoredAd: () => props.sponsoredAd,
	});

	if (landingPageURL.value) {
		trackSraEvent({
			action: SraEventAction.ListClickout,
			sponsoredAd: () => props.sponsoredAd,
		});
	}
	emits('onTrackerClickout', { title: titles.value[0], position: 0 });
}

function onFollowClicked() {
	trackSraEvent({
		action: SraEventAction.FollowClicked,
		sponsoredAd: () => props.sponsoredAd,
	});
}

// Hacks & Exceptions
/** Campaigns with a light colored background that need darker text. */
const highContrastCampaigns = ['TWIXMAS_BURST1_GB_SRA'];
const isHighContrast = computed(() => highContrastCampaigns.includes(props.sponsoredAd.campaign.name));
const biggerFontCampaigns = ['BRITBOX_MULTI-TITLE_BURST1_AU_SRA'];
/** Apply gradient to the title on light backgrounds */
const applyTitleGradientImageAsset = ['WB_Campaign2025_Autumn'];
const isApplyTitleGradient = computed(() =>
	applyTitleGradientImageAsset.some(asset => props.sponsoredAd.campaign.promotionalImageUrl?.includes(asset))
);
/** Campaigns that need a bigger font for the disclaimer text */
const isBiggerFontCampaign = computed(() => biggerFontCampaigns.includes(props.sponsoredAd.campaign.name));

return { __sfc: true,props, emits, trackSraEvent, sponsoredCampaign, campaignNode, promotionalTitle, titles, landingPageURL, isLandingPageEmpty, onSRListPosterClicked, onSRListPosterClickedOut, onSRListClick, onFollowClicked, highContrastCampaigns, isHighContrast, biggerFontCampaigns, applyTitleGradientImageAsset, isApplyTitleGradient, isBiggerFontCampaign, ListToggle, SRTitleListGraphql }
}

})